import React from 'react';
import ReactDOM from 'react-dom/client';
import { CacheProvider } from '@emotion/react';
import { ThemeProvider, createTheme, Theme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import createEmotionCache from './createEmotionCache';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

// Create Emotion cache for MUI style injection
const clientSideEmotionCache = createEmotionCache();

// Helper to map boot theme to MUI theme
const createBootTheme = (bootTheme: any): Theme => {
  // 🔥 CRITICAL: Always use boot theme if available, never fall back to white
  console.log('🎨 MUI: Creating theme from boot theme:', bootTheme);
  
  const defaultColors = {
    primary: '#e67e22',
    secondary: '#27ae60',
    background: '#f8f9fa',
    surface: '#ffffff',
    text: '#212121',
  };

  const colors = bootTheme ? {
    primary: bootTheme.primaryColor || defaultColors.primary,
    secondary: bootTheme.secondaryColor || defaultColors.secondary,
    background: bootTheme.backgroundColor || defaultColors.background,
    surface: bootTheme.surfaceColor || defaultColors.surface,
    text: bootTheme.textColor || defaultColors.text,
  } : defaultColors;
  
  console.log('🎨 MUI: Theme colors:', colors);

  const fontFamily = bootTheme?.fontFamily || '"Inter", "Roboto", "Helvetica", "Arial", sans-serif';
  const borderRadius = bootTheme?.borderRadius ? parseInt(bootTheme.borderRadius) : 8;
  const shadows = bootTheme?.shadows !== false;

  return createTheme({
    palette: {
      primary: {
        main: colors.primary,
      },
      secondary: {
        main: colors.secondary,
      },
      background: {
        default: colors.background,
        paper: colors.surface,
      },
      text: {
        primary: colors.text,
      },
    },
    typography: {
      fontFamily: fontFamily,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: borderRadius,
            boxShadow: shadows ? undefined : 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius,
            boxShadow: shadows ? undefined : 'none',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius,
            boxShadow: shadows ? undefined : 'none',
          },
        },
      },
    },
  });
};

// Load boot theme (set by index.html boot script)
const bootTheme = (window as any).__BOOT_THEME__;
const initialTheme = createBootTheme(bootTheme);

// ✨ PRE-RENDER THEME INJECTION - Backup for CSS variables (primary source is now index.html boot script)
// This code is kept for compatibility but the boot script handles it first
try {
  const cached = localStorage.getItem('personalization-settings');
  if (cached) {
    const settings = JSON.parse(cached);
    const { theme, branding } = settings;
    
    // Apply theme to DOM immediately
    const root = document.documentElement;
    root.style.setProperty('--primary-color', theme.primaryColor);
    root.style.setProperty('--secondary-color', theme.secondaryColor);
    root.style.setProperty('--accent-color', theme.accentColor);
    root.style.setProperty('--background-color', theme.backgroundColor);
    root.style.setProperty('--surface-color', theme.surfaceColor);
    root.style.setProperty('--text-color', theme.textColor);
    root.style.setProperty('--font-family', theme.fontFamily);
    root.style.setProperty('--border-radius', theme.borderRadius);
    
    if (branding?.typography) {
      root.style.setProperty('--heading-font', branding.typography.headingFont);
      root.style.setProperty('--body-font', branding.typography.bodyFont);
    }
    
    if (theme.shadows) {
      root.style.setProperty('--box-shadow', '0 2px 8px rgba(0,0,0,0.1)');
      root.style.setProperty('--card-shadow', '0 4px 12px rgba(0,0,0,0.15)');
    } else {
      root.style.setProperty('--box-shadow', 'none');
      root.style.setProperty('--card-shadow', 'none');
    }
    
    console.log('🎨 PRE-RENDER: Theme applied before React mount (TRUE zero flash)');
  }
} catch (error) {
  console.warn('🎨 PRE-RENDER: Failed to apply cached theme:', error);
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <CacheProvider value={clientSideEmotionCache}>
      <ThemeProvider theme={initialTheme}>
        <CssBaseline />
        <App />
      </ThemeProvider>
    </CacheProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
// Emergency restore deployment Thu Aug 14 14:24:07 CEST 2025
// Folder selector deployment trigger Thu Aug 14 14:45:11 CEST 2025
// File System Access API deployment Thu Aug 14 14:49:11 CEST 2025
